// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import {
  createIndividualAchievement,
  deleteIndividualAchievement,
  editIndividualAchievement,
  getIndividualAchievementDetail,
  getIndividualAchievementList,
  toggleIndividualAchievementActive,
} from '@/modules/individual-achievement/individual-achievement.service'
import type {
  TCreateIndividualAchievementRequest,
  TEditIndividualAchievementRequest,
  TIndividualAchievement,
  TToggleIndividualAchievementActiveRequest,
} from '@/modules/individual-achievement/individual-achievement.types'

export const individualAchievementListQueryOptions = (
  params: TAdminListParams
) =>
  queryOptions({
    queryKey: ['individualAchievementList', params],
    queryFn: () => getIndividualAchievementList(params),
  })

export const individualAchievementDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['individualAchievementDetail', id],
    queryFn: () => getIndividualAchievementDetail(id),
    staleTime: 0,
  })

export function useCreateIndividualAchievement({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TCreateIndividualAchievementRequest>) {
  return useMutation<void, TResponseError, TCreateIndividualAchievementRequest>(
    {
      mutationFn: createIndividualAchievement,
      onSuccess,
      onError,
    }
  )
}

export function useEditIndividualAchievement({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TEditIndividualAchievementRequest>) {
  return useMutation<void, TResponseError, TEditIndividualAchievementRequest>({
    mutationFn: editIndividualAchievement,
    onSuccess,
    onError,
  })
}

export function useToggleIndividualAchievementActive({
  onSuccess,
  onError,
}: TMutation<
  TIndividualAchievement,
  TResponseError,
  TToggleIndividualAchievementActiveRequest
>) {
  return useMutation<
    TIndividualAchievement,
    TResponseError,
    TToggleIndividualAchievementActiveRequest
  >({
    mutationFn: toggleIndividualAchievementActive,
    onSuccess,
    onError,
  })
}

export function useDeleteIndividualAchievement({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: deleteIndividualAchievement,
    onSuccess,
    onError,
  })
}
